/**
 * openExternal.js — cross-platform "open a URL outside the current SPA
 * route" helper, for pages that CAN'T use the authed in-app viewer
 * (`/app/view/{kind}/{slug}` — see NotificationsBell.jsx / ActionHubBio.jsx
 * for that pattern, used whenever the viewer is already logged in).
 *
 * On the web, a plain `<a target="_blank">` already works — this exists
 * purely for Capacitor native, where `target="_blank"` / `window.open()`
 * are silent no-ops: WKWebView/Android WebView have no "new tab" concept,
 * so a tap on a `target="_blank"` link just... does nothing. Confirmed
 * live on iOS Simulator (Aug 2026) — this is what broke every "See it
 * live" / "Preview" / share-sheet link on native while working fine on
 * web, since they all relied on the browser-only new-tab behavior.
 *
 * Fix: on native, route the same tap through `@capacitor/browser`'s
 * `Browser.open()`, which opens an in-app SFSafariViewController (iOS) /
 * Chrome Custom Tab (Android) — the standard Capacitor pattern for
 * "open a link without leaving the app or needing a new-tab concept".
 */
import { getPublicOrigin } from "./publicOrigin";

export const isNativeCapacitor = () =>
  typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();

const isIosNative = () => {
  try {
    return isNativeCapacitor() && window.Capacitor?.getPlatform?.() === "ios";
  } catch { return false; }
};

// SPA navigation without a router hook in scope — pushState + popstate
// fires react-router, exactly like pushNotifications.js's native
// tap-to-navigate handler already does for push notification deep links.
const navigateInApp = (path) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};

/**
 * Open `url` in a new tab (web) or an in-app browser sheet (native).
 * Safe to call directly from an onClick handler.
 *
 * `url` may be relative (`/u/slug`) or absolute — relative paths are
 * resolved against `getPublicOrigin()` first. This matters specifically
 * for native: `Browser.open()` launches a real system browser sheet
 * OUTSIDE the app's own WebView, so a bare relative path has no origin
 * to resolve against (and `window.location.origin` inside the WebView
 * is `capacitor://localhost` / `https://localhost`, not the real public
 * domain — see publicOrigin.js).
 */
export async function openExternalUrl(url) {
  if (!url) return;
  const absolute = /^https?:\/\//i.test(url)
    ? url
    : `${getPublicOrigin()}${url.startsWith("/") ? "" : "/"}${url}`;

  // v146.8 — iOS-only fallback for cleartext http:// targets. Confirmed
  // live: SFSafariViewController (what Browser.open() uses on iOS)
  // categorically refuses to load ANY http:// URL and shows a permanently
  // blank sheet — this is Apple's OWN ATS enforcement inside Safari, which
  // is separate from and NOT overridable by the host app's Info.plist ATS
  // exceptions (those only cover the app's own WKWebView). This only
  // affects --env=local iOS builds, where REACT_APP_PUBLIC_BASE_URL is
  // baked as http://<lan-ip>:<port> for local testing — production always
  // uses the real https://linkola.app domain and never hits this branch.
  // Since the target is always a route this same SPA already renders (the
  // public /u/:slug pages etc.), fall back to in-app navigation instead of
  // a doomed external-browser attempt — same pushState+popstate trick
  // pushNotifications.js uses for native tap-routing. Android's Custom
  // Tabs (Chrome) don't have this restriction — confirmed working live on
  // a real device — so this branch is iOS-only and doesn't touch Android.
  if (isIosNative() && /^http:\/\//i.test(absolute)) {
    try {
      const u = new URL(absolute);
      navigateInApp(u.pathname + u.search + u.hash);
      return;
    } catch (e) {
      console.warn("[openExternal] iOS cleartext in-app fallback failed", e);
      // fall through to the normal native path below as a last resort
    }
  }

  if (isNativeCapacitor()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: absolute });
    } catch (e) {
      // v146.7 — Do NOT fall back to window.open() here. Confirmed live:
      // in a Capacitor WKWebView, a JS-invoked window.open() with no
      // WKUIDelegate popup handler navigates the CURRENT webview to the
      // target URL instead of silently no-opping — i.e. it replaces the
      // entire SPA with the external page, with no back button, exactly
      // the "blank screen, no way back" bug this was meant to fix. A
      // failed native open should surface as an error, not risk destroying
      // the app's own navigation state.
      console.warn("[openExternal] Browser.open failed", e);
      const { toast } = await import("sonner");
      toast.error("Couldn't open that link. Please try again.");
    }
    return;
  }
  window.open(absolute, "_blank", "noopener,noreferrer");
}

/**
 * Convenience onClick handler for an `<a href={url} target="_blank">` (or
 * a `<Link to={url} target="_blank">`) that should keep working exactly
 * as before on web (right-click / middle-click / "open in new tab" all
 * still work since the real `href` is left in place) but route through
 * `openExternalUrl` on native, where the browser-only behaviors don't
 * apply anyway (touch-only, no middle click).
 */
export function externalLinkClickHandler(url, onClickAlso) {
  return (e) => {
    onClickAlso?.(e);
    if (isNativeCapacitor()) {
      e.preventDefault();
      openExternalUrl(url);
    }
  };
}
